import { Sun, Cloud, CloudSun, CloudRain, CloudLightning, CloudSnow, CloudFog, Wind, Droplets } from 'lucide-react';
import { RAIN_ALERT_THRESHOLD } from './useOkcWeather';

const describe = (code) => {
  if (code === 0) return ['Clear skies', Sun];
  if (code <= 2) return ['Partly cloudy', CloudSun];
  if (code === 3) return ['Cloudy', Cloud];
  if (code <= 48) return ['Foggy', CloudFog];
  if (code <= 67 || (code >= 80 && code <= 82)) return ['Rain', CloudRain];
  if (code <= 77 || code === 85 || code === 86) return ['Snow', CloudSnow];
  return ['Thunderstorms', CloudLightning];
};

const mowingTip = (rain, wind) => {
  if (rain >= RAIN_ALERT_THRESHOLD) return 'Rain likely — plan jobs around it.';
  if (wind >= 20) return 'Windy today — watch the clippings.';
  return 'Great mowing weather!';
};

export const WeatherCard = ({ data, failed }) => {
  if (failed) {
    return <div data-testid="weather-card" className="bg-white rounded-2xl p-5 shadow-sm text-sm text-stone-500">Weather is unavailable right now.</div>;
  }
  if (!data?.current) {
    return <div data-testid="weather-card" className="bg-white rounded-2xl p-5 shadow-sm h-40 animate-pulse" />;
  }

  const [label, Icon] = describe(data.current.weather_code);
  const rain = data.daily.precipitation_probability_max[0] ?? 0;
  const wind = Math.round(data.current.wind_speed_10m);

  return (
    <div data-testid="weather-card" className="bg-gradient-to-br from-sky-50 to-lime-50 rounded-2xl p-5 shadow-sm border border-lime-100">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-stone-500 font-medium">OKC Weather Today</p>
          <p className="text-4xl font-bold text-stone-900 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="weather-temp">
            {Math.round(data.current.temperature_2m)}°F
          </p>
          <p className="text-sm text-stone-600">{label} · H {Math.round(data.daily.temperature_2m_max[0])}° L {Math.round(data.daily.temperature_2m_min[0])}°</p>
        </div>
        <Icon className="h-12 w-12 text-amber-400" />
      </div>
      <div className="flex gap-4 mt-3 text-xs text-stone-600">
        <span className="flex items-center gap-1"><Droplets className="h-3.5 w-3.5 text-sky-500" />{rain}% rain</span>
        <span className="flex items-center gap-1"><Wind className="h-3.5 w-3.5 text-stone-400" />{wind} mph</span>
      </div>
      <p className="mt-3 text-sm font-semibold text-green-700" data-testid="weather-tip">{mowingTip(rain, wind)}</p>
      {data.daily.time?.length > 1 && <TomorrowRow daily={data.daily} />}
    </div>
  );
};

const TomorrowRow = ({ daily }) => {
  const [label, Icon] = describe(daily.weather_code[1]);
  const rain = daily.precipitation_probability_max[1] ?? 0;
  return (
    <div data-testid="weather-tomorrow" className="mt-4 pt-4 border-t border-lime-200 flex items-center gap-3">
      <Icon className="h-8 w-8 text-amber-400 shrink-0" />
      <div className="flex-1">
        <p className="text-xs uppercase tracking-wide text-stone-500 font-medium">Tomorrow</p>
        <p className="text-sm text-stone-800 font-semibold">{label} · H {Math.round(daily.temperature_2m_max[1])}° L {Math.round(daily.temperature_2m_min[1])}°</p>
      </div>
      <span className={`flex items-center gap-1 text-xs font-medium ${rain >= RAIN_ALERT_THRESHOLD ? 'text-sky-700' : 'text-stone-600'}`} data-testid="weather-tomorrow-rain">
        <Droplets className="h-3.5 w-3.5 text-sky-500" />{rain}%
      </span>
    </div>
  );
};
